import * as React from 'react';
import { useState, useEffect } from 'react';
import { Link } from 'gatsby';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { Search as SearchIcon } from 'lucide-react';

import { Input } from '../ui/input';
import HeaderSearchBlock from './blocks/header-search.block';
import SearchForm from './forms/search-form';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import { Button } from '../ui/button';

type SearchProps = {
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
};

const Search = ({ isOpen, setIsOpen }: SearchProps) => {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === '/' && !isOpen) {
        event.preventDefault();
        setIsOpen(true);
      }
      if (event.metaKey && event.key === 'k') {
        setIsOpen(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger className='relative'>
        <SearchIcon className='absolute left-2.5 top-2 h-4 w-4 text-muted-foreground' />
        <Input
          type='search'
          placeholder='Search...'
          className='pl-8 h-8 focus-visible:ring-offset-1 focus-visible:ring-1'
        />
      </DialogTrigger>
      <DialogPortal>
        <DialogOverlay className='data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/20 dark:bg-black/70' />
        <DialogContent className='data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] fixed left-[50%] top-[50%] z-50 grid max-h-[85vh] w-[90vw] max-w-3xl translate-x-[-50%] translate-y-[-50%] overflow-hidden border bg-slate-100 dark:bg-slate-700 shadow-lg duration-300 sm:rounded'>
          <VisuallyHidden.Root>
            <DialogTitle>Search</DialogTitle>
            <DialogDescription>Start typing to search the documentation</DialogDescription>
          </VisuallyHidden.Root>
          <React.Suspense
            fallback={<div class='h-6 w-6 animate-spin rounded-full border-b-2 border-current' />}
          >
              <SearchForm />
          </React.Suspense>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
};

export default Search;
